'use client'

import { CardsGroupBlock as CardsGroupBlockType } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { Media } from '../Media/Media'
import { CMSLink } from '../CMSLink'
import { bagelFatOne, quicksand } from '@/utilities/fontImport'
import { useRouter, useSearchParams } from 'next/navigation'

type CardItem = NonNullable<CardsGroupBlockType['cards']>[number]

export const CardsGroupCard: React.FC<{
  card: CardItem
  index: number
  total: number
  displayMode: CardsGroupBlockType['displayMode']
}> = ({ card, index, total, displayMode }) => {
  const { title, media, hoverMedia, link, categoryHandle, enableLink } = card
  const hasHoverMedia = hoverMedia && typeof hoverMedia === 'object'

  const router = useRouter()
  const searchParams = useSearchParams()
  const currentCategory = searchParams.get('category')
  const isActive =
    !!categoryHandle && (currentCategory ? currentCategory === categoryHandle : index === 0)

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!categoryHandle) return

    const params = new URLSearchParams(window.location.search)
    params.set('category', categoryHandle)
    router.push(`?${params.toString()}`, { scroll: false })

    let node = event.currentTarget.closest('section')
    while (node && !node.nextElementSibling && node.parentElement) {
      node = node.parentElement
    }
    node?.nextElementSibling?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const isFirst = index === 0
  const isLast = index === total - 1
  const isEven = index % 2 === 0

  const gradientClasses = isFirst
    ? 'bg-linear-to-b from-pink to-primary'
    : isLast
      ? 'bg-linear-to-b from-light-red to-primary'
      : isEven
        ? 'bg-linear-to-b from-secondary to-light-red'
        : 'bg-linear-to-b from-secondary to-pink'

  return (
    <div
      onClick={categoryHandle ? handleClick : undefined}
      className={cn(
        'group relative z-10 flex flex-col items-center gap-3 rounded-3xl p-6 text-center transition-transform duration-300 ease-out lg:hover:-translate-y-8',
        categoryHandle && 'cursor-pointer',
        isActive && 'ring-4 ring-white ring-offset-4 ring-offset-transparent lg:-translate-y-8',
        displayMode === 'slider' ? 'w-64 shrink-0 sm:w-72' : 'w-full',
        gradientClasses,
      )}
    >
      {title && (
        <span className={cn('text-xl font-bold text-white', bagelFatOne.className)}>{title}</span>
      )}
      {link && enableLink && (
        <span onClick={(e) => e.stopPropagation()}>
          <CMSLink
            {...link}
            iconClassName="ml-2 md:ml-6"
            textClassName={quicksand.className}
            className="px-1.5 py-0.5 md:p-5"
          />
        </span>
      )}

      <div className="relative mt-2 aspect-[3/4] w-full">
        {media && typeof media === 'object' && (
          <Media
            resource={media}
            fill
            imgClassName={cn(
              'object-contain transition-opacity duration-300',
              hasHoverMedia && 'group-hover:opacity-0',
            )}
          />
        )}
        {hasHoverMedia && (
          <Media
            resource={hoverMedia}
            fill
            className="absolute inset-0"
            imgClassName="object-contain opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          />
        )}
      </div>
    </div>
  )
}
